import { useCallback, useState } from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";

import Button from "@components/buttons/Button";
import ProgressCircle from "@components/ProgressCircle";
import styles from "@components/schedule/ScheduleSession.module.css";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import Heading1 from "@components/typography/Heading1";
import { ConsultDurationEnum } from "@typing/Generated";
import { clickCurrentTarget } from "@utils/keyboardUtils";

type Props = {
  duration: ConsultDurationEnum | undefined;
  onDurationSelected: (duration: ConsultDurationEnum) => void;
};

const Duration = ({ duration, onDurationSelected }: Props) => {
  const [editDuration, setEditDuration] = useState<ConsultDurationEnum | undefined>(duration);

  const generateHandleDurationSelect = useCallback(
    (d: ConsultDurationEnum) => () => {
      setEditDuration(d);
    },
    []
  );

  const handleNext = useCallback(() => {
    if (editDuration) {
      onDurationSelected(editDuration);
    }
  }, [editDuration, onDurationSelected]);

  return (
    <div className={styles.centerContent}>
      <Heading1>
        <FormattedMessage id="pages.consults.duration.sessionLength" />
      </Heading1>
      <div className={styles.durationOptions}>
        <div
          className={styles.cursor}
          data-test="30-minutes"
          onClick={generateHandleDurationSelect(ConsultDurationEnum.THIRTY_MINUTES)}
          onKeyUp={clickCurrentTarget}
          tabIndex={0}
        >
          <ProgressCircle
            percents={50}
            size={150}
            type={editDuration === ConsultDurationEnum.THIRTY_MINUTES ? "set" : "hover"}
          >
            <div className={styles.durationNumber}>
              <Heading1>
                <FormattedNumber value={30} />
              </Heading1>
            </div>
            <BodyCopy color="primary">
              <FormattedMessage id="pages.consults.duration.minutes" />
            </BodyCopy>
          </ProgressCircle>
        </div>

        <div
          className={styles.cursor}
          data-test="60-minutes"
          onClick={generateHandleDurationSelect(ConsultDurationEnum.ONE_HOUR)}
          onKeyUp={clickCurrentTarget}
          tabIndex={0}
        >
          <ProgressCircle
            percents={100}
            size={150}
            type={editDuration === ConsultDurationEnum.ONE_HOUR ? "set" : "hover"}
          >
            <div className={styles.durationNumber}>
              <Heading1>
                <FormattedNumber value={60} />
              </Heading1>
            </div>
            <BodyCopy color="primary">
              <FormattedMessage id="pages.consults.duration.minutes" />
            </BodyCopy>
          </ProgressCircle>
        </div>
      </div>
      <div className={editDuration ? "" : styles.hidden}>
        <Button onClick={handleNext} size="large">
          <FormattedMessage id="pages.consults.duration.selectDateAndTime" />
          <IconBase icon="chevronDown" slot="end" />
        </Button>
      </div>
    </div>
  );
};

export default Duration;
